import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  IconButton,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";

interface SelectionCounts {
  deletableCount: number;
  hideableCount: number;
}

interface DeleteConfirmDialogProps {
  open: boolean;
  onClose: () => void;
  onConfirm: () => void;
  selectedCount: number;
  selectionCounts: SelectionCounts;
  isDeleting?: boolean;
}

export const DeleteConfirmDialog = ({
  open,
  onClose,
  onConfirm,
  selectedCount,
  selectionCounts,
  isDeleting = false
}: DeleteConfirmDialogProps) => {
  const { deletableCount, hideableCount } = selectionCounts;

  // Determine dialog title and content based on selection types
  const getDialogContent = () => {
    if (deletableCount > 0 && hideableCount > 0) {
      return (
        <>
          <Typography variant="body1" sx={{ fontWeight: 400 }}>
            You're about to manage {selectedCount} availabilities:
          </Typography>
          <Typography variant="body1" sx={{ mt: 1 }}>
            • {deletableCount} will be deleted
          </Typography>
          <Typography variant="body1">
            • {hideableCount} will be hidden (FareHarbor)
          </Typography>
          <Typography variant="body1" sx={{ mt: 1 }}>
            Proceed?
          </Typography>
        </>
      );
    } else if (hideableCount > 0) {
      return (
        <>
          <Typography variant="body1" sx={{ fontWeight: 400 }}>
            You're about to hide {selectedCount} FareHarbor availabilities.
          </Typography>
          <Typography variant="body1">
            These will be hidden from your calendar but not deleted from FareHarbor.
          </Typography>
          <Typography variant="body1" sx={{ mt: 1 }}>
            Proceed?
          </Typography>
        </>
      );
    } else {
      return (
        <>
          <Typography variant="body1" sx={{ fontWeight: 400 }}>
            You're about to delete {selectedCount} availabilities.
          </Typography>
          <Typography variant="body1">
            Proceed?
          </Typography>
        </>
      );
    }
  };

  // Determine button text
  const getConfirmButtonText = () => {
    if (isDeleting) {
      if (deletableCount > 0 && hideableCount > 0) {
        return 'Processing...';
      } else if (hideableCount > 0) {
        return 'Hiding...';
      } else {
        return 'Deleting...';
      }
    } else {
      if (deletableCount > 0 && hideableCount > 0) {
        return 'Delete/Hide';
      } else if (hideableCount > 0) {
        return 'Hide';
      } else {
        return 'Delete';
      }
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="xs"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
          boxShadow: '0 4px 20px rgba(0,0,0,0.15)'
        }
      }}
    >
      <IconButton
        aria-label="close"
        onClick={onClose}
        sx={{
          position: "absolute",
          right: 8,
          top: 8,
        }}
      >
        <CloseIcon />
      </IconButton>

      <DialogContent sx={{ textAlign: 'center', py: 2 }}>
        {getDialogContent()}
      </DialogContent>

      <DialogActions sx={{
        justifyContent: 'center',
        gap: 2,
        pb: 3,
        px: 3
      }}>
        <Button
          variant="contained"
          onClick={onConfirm}
          disabled={isDeleting}
          sx={{
            borderRadius: 2,
            textTransform: 'none',
            fontWeight: 600,
            px: 5,
            minWidth: 100
          }}
        >
          {getConfirmButtonText()}
        </Button>
        <Button
          variant="outlined"
          color="inherit"
          onClick={onClose}
          disabled={isDeleting}
          sx={{
            borderRadius: 2,
            textTransform: 'none',
            fontWeight: 600,
            px: 3,
            minWidth: 100
          }}
        >
          Cancel
        </Button>
      </DialogActions>
    </Dialog>
  );
};

interface DeleteSuccessDialogProps {
  open: boolean;
  onClose: () => void;
  deletedCount: number;
}

export const DeleteSuccessDialog = ({
  open,
  onClose,
  deletedCount
}: DeleteSuccessDialogProps) => {
  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="xs"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
          boxShadow: '0 4px 20px rgba(0,0,0,0.15)'
        }
      }}
    >
      <IconButton
        aria-label="close"
        onClick={onClose}
        sx={{
          position: "absolute",
          right: 8,
          top: 8,
        }}
      >
        <CloseIcon />
      </IconButton>

      <DialogContent sx={{ textAlign: 'center', py: '48px' }}>
        <Typography variant="body1" sx={{ fontWeight: 500 }}>
          {deletedCount} Availabilities has been deleted.
        </Typography>
      </DialogContent>
    </Dialog>
  );
};
